import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  IconButton,
  Button,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Modal,
  TextField,
  CircularProgress,
  Grid,
  Paper,
  Chip,
  InputAdornment,
  Checkbox
} from "@mui/material";
import {
  Edit as EditIcon,
  Block as BlockIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import Header from "../../../components/Header";
import { tokens } from "../../../theme";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import {
  fetchExpensesData,
  fetchExpenseById,
  fetchAccountSubCategory,
} from "../../../data/expensesData";
import Swal from "sweetalert2";
import { companyManagementUrl, AccountsandFinanceurl, salesManagement, setupManagementUrl } from "../../../config";
import { allBranches, allRegions, PaymentMethodQuery, allCompanyBranchesView, ExpensePaymentMethodsQuery } from "../../../data/Axios/queries";
import { graphqlQuery, generateAndExecuteMutation } from "../../../data/Axios/DynamicService";
import { toggleExpenseStatus } from "../../../data/expensesData";
import ShowMoreDialog from "../../../data/DynamicServiceNew/dynamicDialog";

const Expense = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = parseInt(decodedToken.CompanyId);
  const regionId = parseInt(decodedToken.RegionId);
  const branchId = parseInt(decodedToken.BranchId);

  // States
  const [expenseData, setExpenseData] = useState([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  // Show More Dialog States
  const [showMoreOpen, setShowMoreOpen] = useState(false);
  const [selectedExpenseData, setSelectedExpenseData] = useState(null);

  // Dropdowns
  const [accountSubcategory, setAccountSubCategory] = useState([]);
  const [regions, setRegions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  // const [paymentMethodBridge, setPaymentMethodBridge] = useState([]);

  // Filter States
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    accountType: "",
    paymentMethod: "",
    branch: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  // Form
  const [formData, setFormData] = useState({
    expenseName: "",
    amount: "",
    description: "",
    accountSubcategoryId: "",
    regionId: regionId || "",
    branchId: branchId || "",
    paymentMethodId: "",
    paymentAccountId: "",
    expenseDate: "",
  });

  const userId = JSON.parse(localStorage.getItem("user") || "{}")?.id || 1;


  const base_url = AccountsandFinanceurl.uri;
  const companyManagementBaseUrl = companyManagementUrl.uri;
  // const companyManagementBaseUrl = "https://localhost:7239/graphql/";
  const financeBaseUrl = AccountsandFinanceurl.uri;
  const setupBaseUrl = setupManagementUrl.uri;
  const base_url_salesManagement = salesManagement.uri;

  // Refresh data
  const refreshExpenses = async () => {
    try {
      const { expenses, totalExpenses } = await fetchExpensesData();
      setExpenseData(expenses || []);
      setTotalExpenses(totalExpenses || 0);
    } catch (err) {
      console.error("Refresh failed:", err);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [
  expenseResult,
  subcategories,
  regionResponse,
  branchResponse,
  paymentMethodResponse,
] = await Promise.all([
  fetchExpensesData(),
  fetchAccountSubCategory(),
  graphqlQuery(allRegions, companyManagementBaseUrl),
  graphqlQuery(allCompanyBranchesView, companyManagementBaseUrl),
  graphqlQuery(PaymentMethodQuery, financeBaseUrl),
]);

        setExpenseData(expenseResult.expenses || []);
        setTotalExpenses(expenseResult.totalExpenses || 0);
        setAccountSubCategory(subcategories || []);
        setRegions(regionResponse || []);

        // Filter branches based on user's permissions
        let filteredBranchesList = branchResponse || [];
        if (branchId) {
          // If user has specific branch, show only that branch
          filteredBranchesList = filteredBranchesList.filter(b => b.id === branchId);
        } else if (regionId) {
          // If user has region but no specific branch, show branches in that region
          filteredBranchesList = filteredBranchesList.filter(b => b.regionId === regionId);
        }
        setBranches(filteredBranchesList);
        // Normalize payment methods so all call sites can use either id/methodName or label/value safely.
        setPaymentMethods(
          (paymentMethodResponse || []).map((m) => {
            const id = m.id;
            const methodName = m.methodName || m.accountName || "";
            return {
              id,
              methodName,
              label: methodName,
              value: id,
            };
          })
        );
        // AccountSubCategory bridge data (for cascading to payment accounts)
        //setPaymentMethodBridge(bridgeResponse || []);
      } catch (error) {
        Swal.fire("Error", "Failed to load data", "error");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);
  // Add after line 145 (after loading data)
  useEffect(() => {
  }, [expenseData, accountSubcategory, paymentMethods, branches]);
  // Filter branches
  useEffect(() => {
    if (formData.regionId && branches.length > 0) {
      const filtered = branches.filter(b => b.regionId === parseInt(formData.regionId));
      setFilteredBranches(filtered);
      setFormData(prev => ({ ...prev, branchId: "" }));
    } else {
      setFilteredBranches([]);
    }
  }, [formData.regionId, branches]);

  // Load payment accounts
  // Load payment accounts using name-based bridge matching
  // Maps PaymentMethod name (e.g. "Cash") to AccountSubCategory name (e.g. "Cash") to fetch linked accounts
useEffect(() => {
  const loadAccounts = async () => {

    if (!formData.paymentMethodId) {
      setPaymentAccounts([]);
      return;
    }

    try {

      const query = `
      {
        accountsBySubCategoryId(functionparams: { 
          accountSubcategoryId: ${formData.paymentMethodId} 
        }) {
          id
          accountName
        }
      }`;

      const res = await graphqlQuery(query, financeBaseUrl);

      setPaymentAccounts(res || []);

    } catch (err) {
      console.error("Failed to load payment accounts", err);
      setPaymentAccounts([]);
    }

  };

  loadAccounts();

}, [formData.paymentMethodId]);

  const expenseSubCategories = accountSubcategory.filter(sub => sub.accountCategoryId === 2);
  // FILTERING LOGIC - UPDATED TO USE NAMES
  const filteredData = useMemo(() => {
    let filtered = [...expenseData];

    // Search filter - Updated to check actual values
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(row => {
        // Get display names for search
        const accountName = expenseSubCategories.find(c => c.id === row.accountSubCategoryId)?.accountName || '';
        const branchName = branches.find(b => b.id === row.branchId)?.branchName || branches.find(b => b.id === row.branchId)?.name || '';
        const paymentMethodName = row.paymentMethodName || paymentMethods.find(m => m.id === row.paymentMethodId)?.methodName || '';

        return (
          row.id.toString().includes(search) ||
          (row.expenseName?.toLowerCase() || '').includes(search) ||
          (row.description?.toLowerCase() || '').includes(search) ||
          (row.accountName?.toLowerCase() || accountName.toLowerCase()).includes(search) ||
          (row.branchName?.toLowerCase() || branchName.toLowerCase()).includes(search) ||
          (row.paymentMethodName?.toLowerCase() || paymentMethodName.toLowerCase()).includes(search) ||
          (row.createdByName?.toLowerCase() || '').includes(search) ||
          (row.updatedByName?.toLowerCase() || '').includes(search)
        );
      });
    }

    // Account Type filter
    if (filters.accountType) {
      filtered = filtered.filter(row =>
        parseInt(row.accountSubCategoryId) === parseInt(filters.accountType)
      );
    }

    // Payment Method filter — view rows expose paymentMethodName, so compare by normalized names.
    if (filters.paymentMethod) {
      const selectedMethodId = parseInt(filters.paymentMethod, 10);
      filtered = filtered.filter(row => {
        const selectedMethod = paymentMethods.find(
          m => parseInt(m.id, 10) === selectedMethodId
        );
        if (!selectedMethod || !row.paymentMethodName) return false;
        const selectedMethodName = (selectedMethod.methodName || "").trim().toLowerCase();
        const rowMethodName = (row.paymentMethodName || "").trim().toLowerCase();
        return (
          rowMethodName === selectedMethodName ||
          rowMethodName.includes(selectedMethodName) ||
          selectedMethodName.includes(rowMethodName)
        );
      });
    }

    // Branch filter
    if (filters.branch) {
      filtered = filtered.filter(row =>
        parseInt(row.branchId) === parseInt(filters.branch)
      );
    }

    // Status filter
    if (filters.status) {
      if (filters.status === "approved") {
        filtered = filtered.filter(row => row.cancelled !== true);
      } else if (filters.status === "cancelled") {
        filtered = filtered.filter(row => row.cancelled === true);
      }
    }

    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);

      filtered = filtered.filter(row => {
        const expenseDate = row.expenseDate || row.createdDate;
        if (!expenseDate) return false;
        const rowDate = new Date(expenseDate);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter(row => {
        const expenseDate = row.expenseDate || row.createdDate;
        if (!expenseDate) return false;
        const rowDate = new Date(expenseDate);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate <= toDate;
      });
    }


    return filtered;
  }, [expenseData, searchText, filters, expenseSubCategories, branches, paymentMethods]);

  // Calculate filtered total
  const filteredTotal = useMemo(() => {
    return filteredData.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  }, [filteredData]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(v => v !== "").length;
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearAllFilters = () => {
    setSearchText("");
    setFilters({
      accountType: "",
      paymentMethod: "",
      branch: branchId ? branchId.toString() : "", // Keep user's branch if exists      status: "",
      dateFrom: "",
      dateTo: "",
    });
  };
  // Add this effect to pre-select the branch filter
  useEffect(() => {
    if (branchId && branches.length > 0) {
      // Check if the user's branch exists in the branches list
      const userBranchExists = branches.some(b => b.id === branchId);
      if (userBranchExists) {
        setFilters(prev => ({ ...prev, branch: branchId.toString() }));
      }
    }
  }, [branches, branchId]);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsEditing(false);
    setFormData({
      expenseName: "", amount: "", description: "", accountSubcategoryId: "",
      regionId: "", branchId: "", paymentMethodId: "", paymentAccountId: "", expenseDate: "",
    });
    setFilteredBranches([]);
    setPaymentAccounts([]);
  };

const handleAddExpense = async (e) => {
  e.preventDefault();

  const finalFormData = {
    ...formData,
    regionId: isEditing ? formData.regionId : (regionId || formData.regionId),
    branchId: isEditing ? formData.branchId : (branchId || formData.branchId),
  };

  // paymentAccountId is optional when no linked accounts exist
  const hasLinkedAccounts = paymentAccounts.length > 0;
  if (!finalFormData.expenseName.trim() || !finalFormData.amount || !finalFormData.accountSubcategoryId ||
      !finalFormData.regionId || !finalFormData.branchId || !finalFormData.paymentMethodId ||
      (hasLinkedAccounts && !finalFormData.paymentAccountId)) {
    Swal.fire("Warning", "Please fill all required fields", "warning");
    return;
  }

  let finalExpenseDateTime;

  if (isEditing) {
    // EDIT mode – preserve original date & time unless user changes the date
    const originalDateTime = modalData.expenseDate; // e.g. "2025-04-20T14:30:00"
    const userSelectedDate = finalFormData.expenseDate; // YYYY-MM-DD from form

    if (!userSelectedDate) {
      finalExpenseDateTime = originalDateTime;
    } else {
      const originalDatePart = originalDateTime?.split("T")[0];
      const originalTimePart = originalDateTime?.split("T")[1] || "00:00:00";

      if (userSelectedDate === originalDatePart) {
        finalExpenseDateTime = originalDateTime;
      } else {
        finalExpenseDateTime = `${userSelectedDate}T${originalTimePart}`;
      }
    }
  } else {
    // CREATE mode – use selected date + current time
    const now = new Date();
    const currentTime = now.toTimeString().split(" ")[0];
    const todayDate = now.toISOString().split("T")[0];
    const safeCreatedDate = finalFormData.expenseDate?.trim() !== ""
      ? finalFormData.expenseDate
      : todayDate;
    finalExpenseDateTime = `${safeCreatedDate}T${currentTime}`;
  }

  const basePayload = {
    expenseName: finalFormData.expenseName.trim(),
    description: finalFormData.description.trim() || null,
    amount: parseFloat(finalFormData.amount),
    accountSubCategoryId: parseInt(finalFormData.accountSubcategoryId),
    regionId: parseInt(finalFormData.regionId),
    branchId: parseInt(finalFormData.branchId),
    paymentMethodId: parseInt(finalFormData.paymentMethodId),
    paymentAccountId: finalFormData.paymentAccountId ? parseInt(finalFormData.paymentAccountId) : null,
    expenseDate: finalExpenseDateTime,
    createdBy: userId,
  };

  const mutationData = isEditing
    ? { id: modalData.id, ...basePayload, updatedBy: userId }
    : basePayload;

  try {
    const response = await generateAndExecuteMutation(
      isEditing ? "updateExpensesEntries" : "createExpensesEntries",
      mutationData,
      "expensesEntries",
      base_url_salesManagement
    );

    if (Object.values(response)[0] !== null) {
      await refreshExpenses();
      handleCloseModal();
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: `Expense ${isEditing ? "updated" : "created"} successfully.`,
      });
    }
  } catch (error) {
    console.error(error);
    Swal.fire({ icon: "error", title: "Error!", text: "Operation failed." });
  }
};
  const handleOpenEditModal = async (id) => {
    try {
      const data = await fetchExpenseById(id);
      setModalData(data);

      // Find the branch to confirm it exists
      const existingBranch = branches.find(b => b.id === data.branchId);

      // Find paymentMethodId by matching the name from the view to real PaymentMethods
       let paymentMethodId = "";
    if (data.paymentMethodName && paymentMethods.length > 0) {
      // Safely find matching payment method
      const method = paymentMethods.find(m => {
        if (!m.methodName) return false;
        const viewMethodName = data.paymentMethodName ? data.paymentMethodName.toLowerCase() : '';
        const methodName = m.methodName.toLowerCase();
        
        // Check exact match first
        if (methodName === viewMethodName) return true;
        
        // Check if one contains the other
        if (methodName.includes(viewMethodName) || viewMethodName.includes(methodName)) return true;
        
        return false;
      });
      paymentMethodId = method?.id ? method.id.toString() : "";
    }

      // IMPORTANT: Set region first, then branch
      // This ensures filteredBranches gets populated
      const initialFormData = {
        expenseName: data.expenseName || "",
        amount: data.amount ? data.amount.toString() : "",
        description: data.description || "",
        accountSubcategoryId: data.accountSubCategoryId ? data.accountSubCategoryId.toString() : "",
        regionId: data.regionId ? data.regionId.toString() : (regionId ? regionId.toString() : ""),
        branchId: "", // Will be set after filteredBranches is populated
        paymentMethodId: paymentMethodId,
        paymentAccountId: data.paymentAccountId ? data.paymentAccountId.toString() : "",
        expenseDate: data.expenseDate ? data.expenseDate.split("T")[0] : "",
      };
 
      setFormData(initialFormData);

      // Force a re-filter of branches
      if (data.regionId && branches.length > 0) {
        const filtered = branches.filter(b => b.regionId === parseInt(data.regionId));
        setFilteredBranches(filtered);

        // Set branchId after filteredBranches is updated
        setTimeout(() => {
          setFormData(prev => ({
            ...prev,
            branchId: data.branchId ? data.branchId.toString() : ""
          }));
        }, 100);
      }

      setIsEditing(true);
      setEditModalOpen(true);
      setOpenModal(true);
    } catch (err) {
      console.error("Error loading expense:", err);
      Swal.fire("Error", "Failed to load expense", "error");
    }
  };

  // Add this useEffect to handle branch population when filteredBranches changes
  useEffect(() => {
    if (editModalOpen && modalData.branchId && filteredBranches.length > 0) {
      // Check if the branch exists in filteredBranches
      const branchExists = filteredBranches.some(b => b.id === modalData.branchId);
      if (branchExists && formData.branchId === "") {
        setFormData(prev => ({
          ...prev,
          branchId: modalData.branchId.toString()
        }));
      }
    }
  }, [filteredBranches, editModalOpen, modalData.branchId]);


  const handleActionSelect = async (e, id) => {
    const action = e.target.value;
    if (action === "edit") {
      handleOpenEditModal(id);
      return;
    }

    if (action === "showMore") {
      const expense = expenseData.find(exp => exp.id === id);
      if (expense) {
        // Enrich the data with lookups if names are missing
        const enrichedExpense = {
          ...expense,
          paymentMethodName: expense.paymentMethodName || paymentMethods.find(m => m.id === expense.paymentMethodId)?.methodName || "N/A",
          createdByName: expense.createdByName || "N/A",
          updatedByName: expense.updatedByName || "N/A",
          approvedByName: expense.approvedByName || "N/A",
          approvedDate: expense.approvedDate || "N/A",
        };
        setSelectedExpenseData(enrichedExpense);
        setShowMoreOpen(true);
      }
      return;
    }

    const isApproveAction = action === "Approve";

    try {
      // Pass true for cancelled if it's a "Cancel" action, false if it's "Approve"
      await toggleExpenseStatus(id, !isApproveAction);
      await refreshExpenses();
      Swal.fire("Success", `Expense ${isApproveAction ? "Approved" : "Cancelled"}`, "success");
    } catch (err) {
      Swal.fire("Error", "Action failed", "error");
    }
  };


  const formatDate = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (isNaN(date.getTime())) return value;

  return date.toLocaleString('en-GB', {
    timeZone: 'Africa/Nairobi', // ✅ Kenyan time
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};


  const columns = [
    {
      field: "checkbox",
      headerName: "",
      width: 0.1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Checkbox
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            e.stopPropagation(); // Prevent row click
            if (e.target.checked) {
              setSelectedRows(prev => [...prev, row.id]);
            } else {
              setSelectedRows(prev => prev.filter(id => id !== row.id));
            }
          }}
        />
      ),
    },
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "expenseName", headerName: "Expense Name", flex: 2 },
    {
      field: "accountSubCategoryId",
      headerName: "Account Type",
      flex: 2,
      valueFormatter: ({ value }) => {
        return expenseSubCategories.find(c => c.id === value)?.accountName || "N/A";
      }
    },
    // { field: "description", headerName: "Description", flex: 2 },
    { field: "amount", headerName: "Amount (KES)", flex: 2, valueFormatter: ({ value }) => `KES ${Number(value).toLocaleString()}` },
    {
      field: "paymentMethodName",
      headerName: "Payment Method",
      flex: 2,
      valueGetter: (params) => {
        return params.row.paymentMethodName || "N/A";
      }
    },
    {
      field: "branchId",
      headerName: "Branch",
      flex: 2,
      valueGetter: (params) => {
        return params.row.branchName || branches.find(b => b.id === params.value)?.branchName || branches.find(b => b.id === params.value)?.name || "N/A";
      }
    },
  { 
  field: "expenseDate", 
  headerName: "Expense Date", 
  flex: 2, 
  valueFormatter: ({ value }) => formatDate(value)
},
{ 
  field: "createdDate", 
  headerName: "Created Date", 
  flex: 2, 
  valueFormatter: ({ value }) => formatDate(value)
},
// { 
//   field: "updatedDate", 
//   headerName: "Updated Date", 
//   flex: 2, 
//   valueFormatter: ({ value }) => formatDate(value)
// },
    { field: "createdByName", headerName: "Recorded By", flex: 2 },
    // {
    //   field: "updatedByName", headerName: "Updated By", flex: 2,
    //   valueGetter: (params) => {
    //     // Show name if available, else show ID (params.row.updatedBy) as fallback
    //     return params.row.updatedByName || "N/A";
    //   }
    // },
  //  { field: "updatedDate", headerName: "Updated Date", flex: 1, valueFormatter: ({ value }) => value ? new Date(value).toLocaleDateString() : "N/A" },
    {
      field: "cancelled",
      headerName: "Status",
      flex: 2,
      renderCell: ({ row }) => (
        <Typography fontWeight="bold" color={row.cancelled ? "error.main" : "success.main"}>
          {row.cancelled ? "Cancelled" : "Approved"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 2,
      sortable: false,
      renderCell: ({ row }) => {
        const isApproved = row.cancelled === true;
        return (
          <Select size="small" value="" onChange={(e) => handleActionSelect(e, row.id)} displayEmpty>
            <MenuItem value="" disabled>Select Action</MenuItem>
            <MenuItem value="showMore"><VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> Show More</MenuItem>
            <MenuItem value="edit"><EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit</MenuItem>
            <MenuItem value={isApproved ? "Approve" : "Cancel"}>
              {isApproved ? <BlockIcon fontSize="small" sx={{ mr: 1 }} /> : <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />}
              {isApproved ? "Approve" : "Cancel"}
            </MenuItem>
          </Select>
        );
      },
    },
  ];

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>;
  }

  return (
    <Box m="20px">
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
        <Header title="Expense Management" subtitle="Create and manage expenses" />
      </Box>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => { setIsEditing(false); setOpenModal(true); }}>
          Add New Expense
        </Button>
        {selectedRows.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
          // onClick={handleBulkAction}
          >
            Row Selected ({selectedRows.length})
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
          endIcon={activeFilterCount > 0 && <Chip label={activeFilterCount} size="small" color="primary" />}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
        {(searchText || activeFilterCount > 0) && (
          <Button variant="outlined" color="error" startIcon={<ClearIcon />} onClick={clearAllFilters}>
            Clear All
          </Button>
        )}
      </Box>

      {/* Search and Filters Panel */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: colors.primary[400] }}>
        <Grid container spacing={2}>
          {/* Search Bar */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder="Search by name, description, account type, or branch..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText("")}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Column Filters */}
          {showFilters && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Account Type</InputLabel>
                  <Select
                    value={filters.accountType}
                    onChange={(e) => handleFilterChange("accountType", e.target.value)}
                    label="Filter by Account Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    {expenseSubCategories.map(sub => (
                      <MenuItem key={sub.id} value={sub.id}>{sub.accountName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Payment Method</InputLabel>
                  <Select
                    value={filters.paymentMethod}
                    onChange={(e) => handleFilterChange("paymentMethod", e.target.value)}
                    label="Filter by Payment Method"
                  >
                    <MenuItem value="">All</MenuItem>
                    {paymentMethods.map(m => (
  <MenuItem key={m.value} value={m.value}>
    {m.label}
  </MenuItem>
))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Branch</InputLabel>
                  <Select
                    value={filters.branch}
                    onChange={(e) => handleFilterChange("branch", e.target.value)}
                    label="Filter by Branch"
                  >
                    <MenuItem value="">All</MenuItem>
                    {/* Filter branches based on user's branchId */}
                    {branches
                      .filter(b => !branchId || b.id === branchId) // Only show user's branch if branchId exists
                      .map(b => (
                        <MenuItem key={b.id} value={b.id}>{b.branchName || b.name}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    label="Filter by Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From Date"
                  InputLabelProps={{ shrink: true }}
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To Date"
                  InputLabelProps={{ shrink: true }}
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Total Display */}
      <Box p={3} mb={3} bgcolor={colors.primary[400]} borderRadius="12px" boxShadow={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight="bold" color="#4F93D7">
              Total Expenses: KES {totalExpenses.toLocaleString()}
            </Typography>
          </Grid>
          {(searchText || activeFilterCount > 0) && (
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight="bold" color="secondary.main">
                Filtered Total: KES {filteredTotal.toLocaleString()} ({filteredData.length} records)
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Data Grid */}
      <Box height="75vh" sx={{
        "& .MuiDataGrid-root": { border: "none", borderRadius: "8px" },
        "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], color: "#fff" },
        "& .MuiDataGrid-row:hover": { backgroundColor: colors.primary[700] },
      }}>
        <DataGrid
          rows={filteredData}
          columns={columns}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) => {
            // Optional: You can add row click functionality here if needed
          }}
        />
      </Box>

      {/* MODAL */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box sx={{
          position: "fixed", top: 0, right: 0, width: { xs: "100%", sm: 520 }, height: "100%",
          bgcolor: theme.palette.mode === "dark" ? colors.primary[800] : "#fff",
          boxShadow: 24, p: 4, overflowY: "auto",
        }}>
          <Typography variant="h5" fontWeight="bold" mb={4}>
            {isEditing ? "Edit Expense" : "Add New Expense"}
          </Typography>

          {/* Total Expenses Display inside Modal */}
          <Box p={2} mb={4} bgcolor={colors.primary[400]} borderRadius="8px" boxShadow={2}>
            <Typography variant="h6" fontWeight="bold" color="#4F93D7">
              Total Expenses: KES {totalExpenses.toLocaleString()}
            </Typography>
            {(searchText || activeFilterCount > 0) && (
              <Typography variant="h6" fontWeight="bold" color="secondary.main">
                Filtered Total: KES {filteredTotal.toLocaleString()} ({filteredData.length} records)
              </Typography>
            )}
          </Box>

          <form onSubmit={handleAddExpense}>
            {!isEditing && (regionId || branchId) && (
              <Grid item xs={12}>
                <Box sx={{
                  p: 1.5,
                  mb: 2,
                  bgcolor: colors.greenAccent[700],
                  borderRadius: 1,
                  border: `1px solid ${colors.greenAccent[400]}`
                }}>
                  <Typography variant="body2" color="white">
                    <strong>User Details:</strong>
                    {regionId && ` Region: ${regions.find(r => r.id === regionId)?.regionName || regions.find(r => r.id === regionId)?.name || regionId}`}
                    {branchId && ` | Branch: ${branches.find(b => b.id === branchId)?.branchName || branches.find(b => b.id === branchId)?.name || branchId}`}
                    {regionId && branchId && " (These fields are pre-filled from your profile)"}
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Expense Name" name="expenseName" fullWidth required value={formData.expenseName} onChange={handleInputChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Description" name="description" fullWidth multiline rows={3} value={formData.description} onChange={handleInputChange} />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Expense Type</InputLabel>
                  <Select name="accountSubcategoryId" value={formData.accountSubcategoryId} onChange={handleInputChange} label="Account Type">
                    {expenseSubCategories.map(sub => <MenuItem key={sub.id} value={sub.id}>{sub.accountName}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Amount (KES)" name="amount" type="number" fullWidth required value={formData.amount} onChange={handleInputChange} />
              </Grid>
              {/* Region field - only show if not in user context or when editing */}
              {(!regionId || isEditing) && (
                <Grid item xs={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Region</InputLabel>
                    <Select
                      name="regionId"
                      value={formData.regionId}
                      onChange={handleInputChange}
                      label="Region"
                    >
                      {regions.map(r => <MenuItem key={r.id} value={r.id}>{r.regionName || r.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Branch field - only show if not in user context or when editing */}
              {(!branchId || isEditing) && (
                <Grid item xs={6}>
                  <FormControl fullWidth required disabled={!formData.regionId}>
                    <InputLabel>Branch</InputLabel>
                    <Select
                      name="branchId"
                      value={formData.branchId}
                      onChange={handleInputChange}
                      label="Branch"
                      disabled={!formData.regionId}
                    >
                      {filteredBranches.length > 0 ?
                        filteredBranches.map(b => (
                          <MenuItem key={b.id} value={b.id}>{b.branchName || b.name}</MenuItem>
                        ))
                        : <MenuItem disabled>No branches</MenuItem>
                      }
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Payment Method</InputLabel>
                  <Select name="paymentMethodId" value={formData.paymentMethodId} onChange={handleInputChange} label="Payment Method">
                    {paymentMethods.map(m => (
  <MenuItem key={m.value} value={m.value}>
    {m.label}
  </MenuItem>
))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Only show Payment Account if the selected method has linked accounts */}
              {(paymentAccounts.length > 0 || formData.paymentMethodId) && (
                <Grid item xs={6}>
                  <FormControl fullWidth required={paymentAccounts.length > 0} disabled={!formData.paymentMethodId}>
                    <InputLabel>Payment Account</InputLabel>
                    <Select name="paymentAccountId" value={formData.paymentAccountId} onChange={handleInputChange} label="Payment Account">
                      {paymentAccounts.length > 0 ? paymentAccounts.map(acc => (
                        <MenuItem key={acc.id} value={acc.id}>{acc.accountName}</MenuItem>
                      )) : <MenuItem disabled>No linked accounts for this method</MenuItem>}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Expense Date"
                  name="expenseDate"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={formData.expenseDate}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>

            <Box mt={4} display="flex" gap={2} justifyContent="flex-end">
              <Button type="submit" variant="contained" size="large">
                {isEditing ? "Update Expense" : "Add Expense"}
              </Button>
              <Button variant="outlined" size="large" onClick={handleCloseModal}>Cancel</Button>
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Show More Dialog */}
      <ShowMoreDialog
        open={showMoreOpen}
        onClose={() => setShowMoreOpen(false)}
        data={selectedExpenseData}
        title="Expense Details"
        ignoreFields={[
          "isActive",
          "companyId",
          "cancelled",
          "accountSubCategoryId",
          "paymentMethodId",
          "paymentAccountId",
          "regionId",
          "branchId",
          "createdBy",
          "updatedBy"
        ]}
        forceShowFields={["createdByName", "updatedByName"]}
      />
    </Box>
  );
};

export default Expense;
